import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash2, Edit3, Pin, CheckSquare, ChevronDown, ChevronUp, Repeat, SkipForward } from "lucide-react";
import { toast } from "sonner";
import { PriorityBadge } from "@/pages/Today";
import TaskDrawer from "@/components/TaskDrawer";
import AssigneeAvatar from "@/components/AssigneeAvatar";
import { useSettings } from "@/lib/settings";
import SubtaskChecklist from "@/components/SubtaskChecklist";

const PRIO = ["low","normal","high","urgent"];
const STATUS = ["todo","in_progress","done","missed"];

export default function Tasks() {
  const [list, setList] = useState([]);
  const [scope, setScope] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [projects, setProjects] = useState([]);
  const [people, setPeople] = useState([]);
  const [assignee, setAssignee] = useState("__any__");
  const [openTaskId, setOpenTaskId] = useState(null);

  const load = async () => {
    const params = scope === "all" ? {} : { scope };
    const [{ data }, ps, pe] = await Promise.all([api.get("/tasks", { params }), api.get("/projects"), api.get("/people")]);
    setList(data); setProjects(ps.data); setPeople(pe.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [scope]);

  const save = async (f) => {
    try {
      if (editing?.id) await api.put(`/tasks/${editing.id}`, f);
      else await api.post("/tasks", f);
      toast.success("Saved"); setOpen(false); setEditing(null); load();
    } catch { toast.error("Failed"); }
  };
  const toggleDone = async (t) => {
    await api.put(`/tasks/${t.id}`, { status: t.status === "done" ? "todo" : "done" });
    load();
  };
  const togglePin = async (t) => { await api.put(`/tasks/${t.id}`, { is_pinned: !t.is_pinned }); load(); };
  const moveTomorrow = async (t) => { await api.post(`/tasks/${t.id}/move-to-tomorrow`); toast.success("Moved to tomorrow"); load(); };
  const remove = async (id) => { if (!window.confirm("Delete task?")) return; await api.delete(`/tasks/${id}`); load(); };

  const shown = list.filter((t) =>
    assignee === "__any__" ? true
    : assignee === "__unassigned__" ? !t.assignee_person_id
    : t.assignee_person_id === assignee
  );

  return (
    <div className="space-y-6" data-testid="tasks-page">
      <div className="flex flex-col md:flex-row md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Tasks</h1>
          <p className="text-sm text-muted-foreground mt-1">Priority-first task list — pinned items stay put, overdue items surface up.</p>
        </div>
        <div className="md:ml-auto flex items-center gap-2">
          <Select value={assignee} onValueChange={setAssignee}>
            <SelectTrigger className="w-44" data-testid="task-assignee-filter"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="__any__">Anyone</SelectItem>
              <SelectItem value="__unassigned__">Unassigned</SelectItem>
              {people.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={scope} onValueChange={setScope}>
            <SelectTrigger className="w-40" data-testid="task-scope"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All tasks</SelectItem>
              <SelectItem value="today">Today</SelectItem>
              <SelectItem value="tomorrow">Tomorrow</SelectItem>
              <SelectItem value="week">This week</SelectItem>
              <SelectItem value="overdue">Overdue</SelectItem>
            </SelectContent>
          </Select>
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}>
            <DialogTrigger asChild><Button data-testid="add-task-btn" onClick={() => setEditing({})}><Plus className="w-4 h-4 mr-2"/> New task</Button></DialogTrigger>
            <TaskDialog task={editing} onSave={save} projects={projects}/>
          </Dialog>
        </div>
      </div>

      {shown.length === 0 ? (        <Card className="studio-card p-10 text-center">
          <CheckSquare className="w-10 h-10 text-muted-foreground/50 mx-auto mb-3" strokeWidth={1.5}/>
          <div className="text-lg font-medium">All clear</div>
          <p className="text-sm text-muted-foreground mt-1">No tasks match this view. Enjoy the calm.</p>
        </Card>
      ) : (
        <div className="space-y-2">
          {shown.map((t) => (
            <TaskRow
              key={t.id}
              task={t}
              people={people}
              onOpen={() => setOpenTaskId(t.id)}
              onToggleDone={() => toggleDone(t)}
              onTogglePin={() => togglePin(t)}
              onTomorrow={() => moveTomorrow(t)}
              onEdit={() => { setEditing(t); setOpen(true); }}
              onRemove={() => remove(t.id)}
              onTaskChanged={(nt) => setList((prev) => prev.map((x) => (x.id === nt.id ? nt : x)))}
              onSkip={async () => {
                try {
                  const { data } = await api.post(`/tasks/${t.id}/skip-occurrence`);
                  setList((prev) => prev.map((x) => (x.id === data.id ? data : x)));
                  toast.success("Skipped this one — series intact");
                } catch { toast.error("Could not skip"); }
              }}
            />
          ))}
        </div>
      )}

      <TaskDrawer
        taskId={openTaskId}
        open={!!openTaskId}
        onClose={() => setOpenTaskId(null)}
        onChanged={(t) => {
          if (t?._deleted) setList((prev) => prev.filter((x) => x.id !== t.id));
          else setList((prev) => prev.map((x) => (x.id === t.id ? t : x)));
        }}
      />
    </div>
  );
}

function TaskRow({ task: t, people, onOpen, onToggleDone, onTogglePin, onTomorrow, onEdit, onRemove, onTaskChanged, onSkip }) {
  const subs = t.subtasks || [];
  const subsPending = subs.length > 0 && subs.some((s) => !s.done);
  const [expanded, setExpanded] = useState(false);
  return (
    <Card className="studio-card p-4" data-testid={`task-row-${t.id}`}>
      <div className="flex items-center gap-4">
        <button
          onClick={() => { if (t.status !== "done" && subsPending) { setExpanded(true); return; } onToggleDone(); }}
          data-testid={`task-toggle-${t.id}`}
          className={`h-5 w-5 rounded-md border-2 flex items-center justify-center shrink-0 ${t.status === "done" ? "bg-[hsl(var(--studio-primary))] border-[hsl(var(--studio-primary))]" : "border-border"}`}
        >
          {t.status === "done" && <CheckSquare className="w-3.5 h-3.5 text-white"/>}
        </button>
        <div className="flex-1 min-w-0">
          <button type="button" onClick={onOpen} data-testid={`task-open-${t.id}`} className="text-left w-full">
            <div className={`text-sm font-medium truncate ${t.status === "done" ? "line-through text-muted-foreground" : ""}`}>{t.title}</div>
            <div className="flex items-center gap-2 mt-1 text-[11px] text-muted-foreground">
              <PriorityBadge priority={t.priority}/>
              {t.due_date && <span>Due {t.due_date}</span>}
              {t.duration_minutes && <span>· {t.duration_minutes}m</span>}
              {subs.length > 0 && <span>· {subs.filter((s) => s.done).length}/{subs.length} subtasks</span>}
              {t.is_recurring && (
                <span className="inline-flex items-center gap-1 text-[hsl(var(--studio-primary))]" data-testid={`task-recurring-${t.id}`}>
                  <Repeat className="w-3 h-3" /> {t.recurrence_rule || "weekly"}
                </span>
              )}

              {t.is_pinned && <Badge variant="secondary" className="text-[9px] font-normal">pinned</Badge>}
            </div>
          </button>
        </div>
        <div className="flex items-center gap-1">
          <AssigneeAvatar person={people.find((p) => p.id === t.assignee_person_id)} size="md" className="mr-1" />
          {subs.length > 0 && (
            <Button variant="ghost" size="sm" data-testid={`task-expand-${t.id}`} onClick={() => setExpanded((v) => !v)}>
              {expanded ? <ChevronUp className="w-3.5 h-3.5"/> : <ChevronDown className="w-3.5 h-3.5"/>}
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={onTogglePin}><Pin className={`w-3.5 h-3.5 ${t.is_pinned ? "fill-current" : ""}`}/></Button>
          <Button variant="ghost" size="sm" onClick={onTomorrow} data-testid={`task-tomorrow-${t.id}`}>Tomorrow</Button>
          {t.is_recurring && (
            <Button variant="ghost" size="sm" onClick={onSkip} data-testid={`task-skip-${t.id}`} title="Skip this occurrence">
              <SkipForward className="w-3.5 h-3.5"/>
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={onEdit}><Edit3 className="w-3.5 h-3.5"/></Button>
          <Button variant="ghost" size="sm" onClick={onRemove}><Trash2 className="w-3.5 h-3.5"/></Button>
        </div>
      </div>
      {subs.length > 0 && expanded && <SubtaskChecklist task={t} onTaskChanged={onTaskChanged} />}
    </Card>
  );
}

function TaskDialog({ task, onSave, projects }) {
  const [f, setF] = useState({});
  const { settings } = useSettings() || {};
  useEffect(() => {
    setF({
      title: task?.title || "",
      description: task?.description || "",
      priority: task?.priority || "normal",
      status: task?.status || "todo",
      due_date: task?.due_date || "",
      scheduled_for: task?.scheduled_for ? task.scheduled_for.slice(0,16) : "",
      duration_minutes: task?.duration_minutes || 30,
      is_pinned: task?.is_pinned || false,
      project_id: task?.project_id || "",
      schedule_window: task?.schedule_window || "work_hours",
      block_calendar: task?.block_calendar || false,
      is_recurring: task?.is_recurring || false,
      recurrence_rule: task?.recurrence_rule || null,
      assignee_person_id: task?.assignee_person_id || "",
    });
  }, [task]);
  return (
    <DialogContent className="max-w-lg">
      <DialogHeader><DialogTitle>{task?.id ? "Edit task" : "New task"}</DialogTitle></DialogHeader>
      <div className="space-y-3">
        <div className="space-y-1.5"><Label className="text-xs">Title</Label><Input data-testid="task-title" value={f.title || ""} onChange={(e) => setF({...f, title: e.target.value})}/></div>
        <div className="space-y-1.5"><Label className="text-xs">Description</Label><Textarea rows={2} value={f.description || ""} onChange={(e) => setF({...f, description: e.target.value})}/></div>
        <div className="grid grid-cols-3 gap-3">
          <div className="space-y-1.5"><Label className="text-xs">Priority</Label>
            <Select value={f.priority} onValueChange={(v) => setF({...f, priority: v})}>
              <SelectTrigger data-testid="task-priority"><SelectValue/></SelectTrigger>
              <SelectContent>{PRIO.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label className="text-xs">Status</Label>
            <Select value={f.status} onValueChange={(v) => setF({...f, status: v})}>
              <SelectTrigger><SelectValue/></SelectTrigger>
              <SelectContent>{STATUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label className="text-xs">Duration (min)</Label><Input type="number" value={f.duration_minutes} onChange={(e) => setF({...f, duration_minutes: parseInt(e.target.value) || 30})}/></div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label className="text-xs">Due date</Label><Input type="date" value={f.due_date || ""} onChange={(e) => setF({...f, due_date: e.target.value})}/></div>
          <div className="space-y-1.5"><Label className="text-xs">Scheduled for</Label><Input type="datetime-local" value={f.scheduled_for || ""} onChange={(e) => setF({...f, scheduled_for: e.target.value})}/></div>
        </div>
        <div className="grid grid-cols-2 gap-3 items-end">
          <div className="space-y-1.5"><Label className="text-xs">Repeats</Label>
            <Select
              value={f.is_recurring ? (f.recurrence_rule || "weekly") : "none"}
              onValueChange={(v) => setF({ ...f, is_recurring: v !== "none", recurrence_rule: v === "none" ? null : v })}
            >
              <SelectTrigger data-testid="task-recurrence"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Doesn't repeat</SelectItem>
                <SelectItem value="daily">Every day</SelectItem>
                <SelectItem value="weekly">Every week</SelectItem>
                <SelectItem value="fortnightly">Every fortnight</SelectItem>
                <SelectItem value="monthly">Every month</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label className="text-xs">Schedule window</Label>
            <Select value={f.schedule_window || "work_hours"} onValueChange={(v) => setF({...f, schedule_window: v})}>
              <SelectTrigger data-testid="task-schedule-window"><SelectValue/></SelectTrigger>
              <SelectContent>
                {(settings?.schedule_windows || []).map((w) => (
                  <SelectItem key={w.key} value={w.key}>{w.name} · {w.start}–{w.end}</SelectItem>
                ))}
                <SelectItem value="custom">Specific time (custom)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2 h-10">
            <Switch checked={!!f.block_calendar} data-testid="task-block-calendar" onCheckedChange={(v) => setF({...f, block_calendar: v})}/>
            <span className="text-xs text-muted-foreground">Block time on my calendar</span>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 items-end">
          <div className="space-y-1.5"><Label className="text-xs">Project</Label>
            <Select value={f.project_id || "__none__"} onValueChange={(v) => setF({...f, project_id: v === "__none__" ? "" : v})}>
              <SelectTrigger><SelectValue placeholder="—"/></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">No project</SelectItem>
                {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2 h-10">
            <Switch checked={!!f.is_pinned} onCheckedChange={(v) => setF({...f, is_pinned: v})}/> <span className="text-xs text-muted-foreground">Pin to top</span>
          </div>
        </div>
      </div>
      <DialogFooter><Button data-testid="task-save" onClick={() => onSave({
        ...f,
        scheduled_for: f.scheduled_for ? new Date(f.scheduled_for).toISOString() : null,
      })}>Save</Button></DialogFooter>
    </DialogContent>
  );
}
